import { applyTheme, applyThemeConfig } from "@peer-start/theme";
import { Button, Card } from "@peer-start/ui";
import { useEffect, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import {
  useBootstrap,
  usePersonas,
  usePrototypeCommand,
  useSelectSession,
  useSession,
} from "../domains/prototype/api/queries.js";
import { ManagerCourseRankingPage } from "../domains/prototype/pages/ManagerCourseRankingPage.js";
import { ManagerDashboardPage } from "../domains/prototype/pages/ManagerDashboardPage.js";
import { OnboardingPage } from "../domains/prototype/pages/OnboardingPage.js";
import { SchoolDocumentsPage, SchoolPage } from "../domains/prototype/pages/SchoolPage.js";
import { SchoolSetupPage } from "../domains/prototype/pages/SchoolSetupPage.js";
import { UserManagementPage } from "../domains/prototype/pages/UserManagementPage.js";
import { SkillPage } from "../domains/prototype/pages/SkillPage.js";
import { PassportActivityPage } from "../domains/passport/pages/PassportActivityPage.js";
import { AreaAssessmentPage } from "../domains/passport/pages/AreaAssessmentPage.js";
import { LearningChatPage } from "../domains/passport/pages/LearningChatPage.js";
import { PassportDashboardPage } from "../domains/passport/pages/PassportDashboardPage.js";
import { PassportPageDetail } from "../domains/passport/pages/PassportPageDetail.js";
import { ProfilePage } from "../domains/passport/pages/ProfilePage.js";
import { StampDetailPage } from "../domains/passport/pages/StampDetailPage.js";
import { MentorMenteesPage } from "../domains/prototype/pages/MentorMenteesPage.js";
import { isMentor, personaRoleKey, workspaceHome } from "../domains/prototype/data/user-management.js";
import { AppShell } from "./components/AppShell.js";
import { AdminSituationCardDetailPage } from "../domains/administration/pages/AdminSituationCardDetailPage.js";
import { AdminSituationCardsPage } from "../domains/administration/pages/AdminSituationCardsPage.js";
import { useOrganisationTheme } from "../domains/theme-configurator/api/queries.js";
import { LoginGate } from "../domains/auth/components/LoginGate.js";
import { clearProductTourSessionDismissal } from "../domains/passport/components/product-tour-storage.js";
import { SignOut } from "../domains/auth/components/SignOut.js";
import { pageDefinitions } from "./page-references.js";

function AppRoutes() {
  const { t } = useTranslation();
  const personas = usePersonas();
  const session = useSession();
  const authenticated = session.data?.authenticated === true;
  const bootstrap = useBootstrap(authenticated);
  const selectSession = useSelectSession();
  const mutation = usePrototypeCommand();
  const currentPersona = session.data?.authenticated ? session.data.persona : null;
  const organisationId = session.data?.authenticated ? session.data.organisation.id : null;
  const organisationTheme = useOrganisationTheme(organisationId);

  useEffect(() => {
    if (organisationTheme.data) applyThemeConfig(organisationTheme.data.theme);
    else if (bootstrap.data) applyTheme(bootstrap.data.themeId);
  }, [bootstrap.data, organisationTheme.data]);

  if (personas.isLoading || session.isLoading || (authenticated && bootstrap.isLoading))
    return (
      <main className="grid min-h-screen place-items-center bg-background p-6 text-foreground">
        <p className="font-medium">{t("common.loading")}</p>
      </main>
    );
  if (personas.isError || session.isError || (authenticated && bootstrap.isError))
    return (
      <main className="grid min-h-screen place-items-center bg-background p-6 text-foreground">
        <Card className="max-w-md text-center">
          <h1 className="text-xl font-medium">{t("common.error")}</h1>
          <Button className="mt-5" controlId="app.retry" onClick={() => void session.refetch()}>
            {t("common.retry")}
          </Button>
        </Card>
      </main>
    );

  if (!authenticated || !currentPersona) {
    return (
      <main className="grid min-h-screen place-items-center bg-background p-6 text-foreground">
        <Card className="w-full max-w-2xl">
          <p className="text-sm font-medium uppercase tracking-wide text-primary">{t("session.eyebrow")}</p>
          <h1 className="mt-2 text-3xl font-medium">{t("session.title")}</h1>
          <p className="mt-2 text-muted-foreground">{t("session.description")}</p>
          <SignOut />
          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            {personas.data?.map((persona) => (
              <Button
                className="min-h-16 justify-start text-left"
                controlId={`session.select.${persona.id}`}
                disabled={selectSession.isPending}
                key={`${persona.organisationId}:${persona.id}`}
                onClick={() => {
                  clearProductTourSessionDismissal(persona.id);
                  void selectSession.mutateAsync(persona.id);
                }}
                variant="secondary"
              >
                <span>
                  <span className="block font-medium">{persona.name}</span>
                  <span className="block text-xs font-medium opacity-70">
                    {persona.organisationName} · {t(`common.${personaRoleKey(persona)}`)}
                  </span>
                </span>
              </Button>
            ))}
          </div>
        </Card>
      </main>
    );
  }

  if (!bootstrap.data) return null;

  const employee = currentPersona.kind === "employee";
  const defaultRoute = workspaceHome(currentPersona);
  const onboardingComplete = employee && currentPersona.onboardingComplete;
  const employeePage = (page: ReactNode) =>
    employee ? (
      onboardingComplete ? (
        page
      ) : (
        <Navigate replace to="/me/onboarding" />
      )
    ) : (
      <Navigate replace to={defaultRoute} />
    );
  const supervisorPage = (page: ReactNode) => (!employee ? page : <Navigate replace to="/me" />);
  const canReadPlatform =
    session.data?.authenticated === true && session.data.permissions.includes("platform:read");
  const administrationPage = (page: ReactNode) =>
    !employee && canReadPlatform ? page : <Navigate replace to={defaultRoute} />;
  const runCommand = async (command: Parameters<typeof mutation.mutateAsync>[0]) =>
    (await mutation.mutateAsync(command)).message;

  return (
    <Routes>
      <Route
        element={
          <AppShell
            availablePersonas={personas.data ?? bootstrap.data.personas}
            bootstrap={bootstrap.data}
            commandPending={mutation.isPending}
            onPersonaChange={async (persona) => {
              clearProductTourSessionDismissal(persona.id);
              await selectSession.mutateAsync(persona.id);
            }}
            persona={currentPersona}
            runCommand={runCommand}
          />
        }
      >
        <Route index element={<Navigate replace to={defaultRoute} />} />
        <Route path={pageDefinitions.dashboard.path} element={employeePage(<PassportDashboardPage />)} />
        <Route path={pageDefinitions.assessment.path} element={employeePage(<AreaAssessmentPage />)} />
        <Route path={pageDefinitions.chat.path} element={employeePage(<LearningChatPage />)} />
        <Route path={pageDefinitions.passport.path} element={employeePage(<ProfilePage />)} />
        <Route path={pageDefinitions.passportDomain.path} element={employeePage(<PassportPageDetail />)} />
        <Route path={pageDefinitions.stamp.path} element={employeePage(<StampDetailPage />)} />
        <Route path={pageDefinitions.skill.path} element={employeePage(<SkillPage />)} />
        <Route path={pageDefinitions.activity.path} element={employeePage(<PassportActivityPage />)} />
        <Route path={pageDefinitions.profile.path} element={employeePage(<ProfilePage />)} />
        <Route
          path={pageDefinitions.onboarding.path}
          element={employee ? <OnboardingPage /> : <Navigate replace to={defaultRoute} />}
        />
        <Route
          path={pageDefinitions.managerDashboard.path}
          element={supervisorPage(<ManagerDashboardPage />)}
        />
        <Route path={pageDefinitions.managerChat.path} element={supervisorPage(<LearningChatPage />)} />
        <Route
          path={pageDefinitions.managerCourseRanking.path}
          element={supervisorPage(<ManagerCourseRankingPage />)}
        />
        <Route
          path={pageDefinitions.mentorMentees.path}
          element={supervisorPage(
            isMentor(currentPersona) ? (
              <MentorMenteesPage />
            ) : (
              <Navigate replace to={pageDefinitions.managerChat.path} />
            ),
          )}
        />
        <Route path={pageDefinitions.users.path} element={supervisorPage(<UserManagementPage />)} />
        <Route path={pageDefinitions.school.path} element={supervisorPage(<SchoolPage />)} />
        <Route
          path={pageDefinitions.schoolDocuments.path}
          element={supervisorPage(<SchoolDocumentsPage />)}
        />
        <Route path={pageDefinitions.schoolSetup.path} element={supervisorPage(<SchoolSetupPage />)} />
        {[
          pageDefinitions.adminDashboard.path,
          pageDefinitions.adminOrganisations.path,
          pageDefinitions.adminOrganisation.path,
          pageDefinitions.adminTheme.path,
          pageDefinitions.adminUsers.path,
          pageDefinitions.adminUser.path,
          pageDefinitions.adminConfiguration.path,
          pageDefinitions.adminActivity.path,
        ].map((path) => (
          <Route
            key={path}
            path={path}
            element={administrationPage(
              <Navigate replace to={pageDefinitions.adminSituationCards.path} />,
            )}
          />
        ))}
        <Route
          path={pageDefinitions.adminSituationCards.path}
          element={administrationPage(<AdminSituationCardsPage />)}
        />
        <Route
          path={pageDefinitions.adminSituationCard.path}
          element={administrationPage(<AdminSituationCardDetailPage />)}
        />
        <Route path="*" element={<Navigate replace to={defaultRoute} />} />
      </Route>
    </Routes>
  );
}

export function App() {
  return (
    <LoginGate>
      <BrowserRouter basename={import.meta.env.BASE_URL}>
        <AppRoutes />
      </BrowserRouter>
    </LoginGate>
  );
}
